/**
 * Tela /settings/branding — TH-55126 / M-branding.3.
 *
 * Permite ao tenant_admin (do próprio tenant) configurar a identidade
 * visual aplicada nos templates de arte e no admin:
 *   - Logo light (sobre fundo claro) — PNG ≤2MB, dim ≥256×256; ou SVG.
 *   - Logo dark (sobre fundo escuro) — idem.
 *   - Cor primária (hex).
 *   - Cor secundária (hex).
 *
 * Backend (TH-55125):
 *   - GET    /tenants/{id}/branding       hidrata estado inicial
 *   - PATCH  /tenants/{id}/branding       salva cores (JSON)
 *   - POST   /tenants/{id}/branding/logo  upload (multipart)
 *
 * UX:
 *   - Logos: upload imediato ao escolher arquivo (POST → atualiza state).
 *     Cada variant tem seu próprio estado de loading + erro inline.
 *   - Cores: form com Salvar único (PATCH). Preview ao vivo reflete
 *     primary/secondary digitados (antes de salvar).
 *   - Preview ao vivo: dois cards (light + dark) com Button, Badge e
 *     amostra de logo, aplicando `--color-primary` via inline style.
 *
 * Acesso:
 *   - super_admin → mensagem pedindo pra abrir /clients (a tela exige
 *     contexto de 1 tenant). Não construímos seletor aqui — fora do escopo.
 *   - tenant_admin → tela completa.
 *   - demais roles → 403 inline (backend devolve 403 no PATCH).
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { Button, buttonVariants } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  isValidHexColor,
  useTenantBranding,
  useUpdateTenantBranding,
  useUploadTenantLogo,
  validateLogoFile,
  type LogoVariant,
  type TenantBranding,
} from "@/hooks/useTenantBranding";

// Paleta default da marca Priceshow — fallback quando o tenant ainda não definiu
// cor própria. Fonte canônica: design-system/brand-package/tokens/ds.css
// (--primary / --background). Precisam ser hex CONCRETOS: alimentam o
// <input type="color"> e o placeholder, que não aceitam CSS vars. Esta tela é um
// editor/preview de identidade de marca → cores de tema fixo, não do tema do admin.
// aderencia:tema-fixo-inicio
const DEFAULT_PRIMARY = "#fbbf24";
const DEFAULT_SECONDARY = "#0a0e1a";
// aderencia:tema-fixo-fim

// ── Schemas ────────────────────────────────────────────────────────────────

const colorsSchema = z.object({
  brand_primary: z
    .string()
    .trim()
    .refine((v) => v === "" || isValidHexColor(v), {
      message: "Cor deve estar no formato hexadecimal (ex.: #RRGGBB).",
    }),
  brand_secondary: z
    .string()
    .trim()
    .refine((v) => v === "" || isValidHexColor(v), {
      message: "Cor deve estar no formato hexadecimal (ex.: #RRGGBB).",
    }),
});
type ColorsForm = z.infer<typeof colorsSchema>;

// ── LogoUploader ───────────────────────────────────────────────────────────

interface LogoUploaderProps {
  variant: LogoVariant;
  label: string;
  currentUrl: string | null;
  tenantId: string;
}

function LogoUploader({ variant, label, currentUrl, tenantId }: LogoUploaderProps) {
  const upload = useUploadTenantLogo(tenantId);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputId = `logo-${variant}`;

  // Fundo fixo do preview do logo: branco pro claro, escuro pro dark — mostra o
  // logo como aparecerá, independente do tema atual do admin. aderencia:tema-fixo
  const previewBg = variant === "light" ? "#ffffff" : "#0a0e1a"; // aderencia:tema-fixo

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    setLocalError(null);
    upload.reset();
    const file = ev.target.files?.[0];
    if (!file) return;
    const err = await validateLogoFile(file);
    if (err) {
      setLocalError(err.message);
      ev.target.value = "";
      return;
    }
    try {
      await upload.mutateAsync({ variant, file });
    } catch {
      // erro do backend cai em upload.error
    }
    // Limpa o input pra permitir re-upload do mesmo arquivo após erro.
    ev.target.value = "";
  }

  const errorMessage =
    localError ??
    (upload.isError
      ? upload.error instanceof Error
        ? upload.error.message
        : "Erro ao enviar logo."
      : null);

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>

      <div
        className="flex h-32 w-full items-center justify-center rounded-md border border-[var(--color-border)]"
        style={{ background: previewBg }}
        data-testid={`logo-preview-${variant}`}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={`Pré-visualização do logo ${variant === "light" ? "claro" : "escuro"}`}
            className="max-h-24 max-w-[80%] object-contain"
          />
        ) : (
          <span className="text-xs text-[var(--color-muted-foreground)]">
            Nenhum logo enviado.
          </span>
        )}
      </div>

      <input
        id={inputId}
        type="file"
        accept="image/png,image/svg+xml"
        onChange={onPick}
        disabled={upload.isPending}
        aria-describedby={`${inputId}-hint`}
        className="block w-full text-xs text-[var(--color-muted-foreground)] file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-secondary)] file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-[var(--color-secondary-foreground)] hover:file:bg-[var(--color-accent)]"
      />

      <p
        id={`${inputId}-hint`}
        className="text-xs text-[var(--color-muted-foreground)]"
      >
        PNG ou SVG, máximo 2 MB. PNG precisa ter pelo menos 256×256px.
      </p>

      {upload.isPending ? (
        <p className="text-xs text-[var(--color-muted-foreground)]" role="status">
          Enviando…
        </p>
      ) : null}

      {errorMessage ? (
        <p className="text-xs text-[var(--color-destructive)]" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {upload.isSuccess && !localError ? (
        <p className="text-xs text-[var(--color-success)]" role="status">
          Logo {variant === "light" ? "claro" : "escuro"} atualizado.
        </p>
      ) : null}
    </div>
  );
}

// ── ColorPicker (hex + native picker) ──────────────────────────────────────

interface ColorFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  fallbackPreview: string;
}

function ColorField({
  id,
  label,
  hint,
  value,
  onChange,
  error,
  fallbackPreview,
}: ColorFieldProps) {
  const safeValue = isValidHexColor(value) ? value : fallbackPreview;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          aria-label={`${label} (seletor visual)`}
          value={safeValue}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 cursor-pointer rounded-md border border-[var(--color-border-strong)] bg-[var(--color-card)]"
        />
        <Input
          id={id}
          type="text"
          inputMode="text"
          maxLength={7}
          placeholder={fallbackPreview}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="font-mono"
        />
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-[var(--color-muted-foreground)]">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="text-xs text-[var(--color-destructive)]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── BrandingPreview ────────────────────────────────────────────────────────

interface BrandingPreviewProps {
  primary: string;
  secondary: string;
  branding: TenantBranding;
}

/**
 * Renderiza dois mini-cards (light/dark) com primary aplicada em
 * Button + Badge + amostra de logo. Usa `--color-primary` override
 * inline. Não muda o tema global do admin — apenas o container preview.
 */
function BrandingPreview({ primary, secondary, branding }: BrandingPreviewProps) {
  const primaryHex = isValidHexColor(primary) ? primary : DEFAULT_PRIMARY;
  const secondaryHex = isValidHexColor(secondary) ? secondary : DEFAULT_SECONDARY;

  // Heurística simples pro `primary-foreground`: usa branco se primary
  // for escuro, preto se for claro. Evita texto invisível no botão.
  const fg = readableForeground(primaryHex);

  const styleOverride = {
    "--color-primary": primaryHex,
    "--color-primary-foreground": fg,
    "--color-secondary": secondaryHex,
  } as React.CSSProperties;

  return (
    <Card>
      <h2 className="mb-1 text-base font-semibold text-[var(--color-foreground)]">
        Preview
      </h2>
      <p className="mb-4 text-xs text-[var(--color-muted-foreground)]">
        Como os botões e selos vão aparecer com essa identidade — claro e escuro.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PreviewCard
          theme="light"
          logoUrl={branding.logo_light_url}
          style={styleOverride}
        />
        <PreviewCard
          theme="dark"
          logoUrl={branding.logo_dark_url}
          style={styleOverride}
        />
      </div>
    </Card>
  );
}

function PreviewCard({
  theme,
  logoUrl,
  style,
}: {
  theme: "light" | "dark";
  logoUrl: string | null;
  style: React.CSSProperties;
}) {
  const isDark = theme === "dark";
  const bg = isDark ? "#0a0e1a" : "#ffffff";
  const fg = isDark ? "#fafafa" : "#0a0e1a";
  const mutedFg = isDark ? "#9aa3b1" : "#6b7280";
  return (
    <div
      data-theme={theme}
      data-testid={`preview-${theme}`}
      className="rounded-md border border-[var(--color-border)] p-5"
      style={{ background: bg, color: fg, ...style }}
    >
      <div className="mb-4 flex h-12 items-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`Logo ${isDark ? "escuro" : "claro"}`}
            className="max-h-10 max-w-[60%] object-contain"
          />
        ) : (
          <span className="text-xs" style={{ color: mutedFg }}>
            (sem logo {isDark ? "escuro" : "claro"})
          </span>
        )}
      </div>
      <div className="mb-4 space-y-1">
        <p className="text-sm font-semibold" style={{ color: fg }}>
          Suba a planilha.
        </p>
        <p className="text-xs" style={{ color: mutedFg }}>
          Suas TVs se viram.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm">Salvar</Button>
        <Badge
          variant="muted"
          style={{
            background: `color-mix(in srgb, var(--color-primary) 18%, transparent)`,
            color: "var(--color-primary)",
          }}
        >
          Em destaque
        </Badge>
      </div>
    </div>
  );
}

/** Heurística WCAG aproximada: luminância > 0.55 → texto escuro. */
function readableForeground(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  // Luminância perceptual (BT.601) — barata e boa o bastante pra fg/bg.
  const l = 0.299 * r + 0.587 * g + 0.114 * b;
  // Hex concreto aplicado como --color-primary-foreground no preview de marca
  // (tema fixo da arte, independente do tema do admin). aderencia:tema-fixo
  return l > 0.55 ? "#0a0e1a" : "#fafafa"; // aderencia:tema-fixo
}

// ── ColorsForm + página ────────────────────────────────────────────────────

interface ColorsCardProps {
  tenantId: string;
  branding: TenantBranding;
  onPreviewChange: (primary: string, secondary: string) => void;
}

function ColorsCard({ tenantId, branding, onPreviewChange }: ColorsCardProps) {
  const update = useUpdateTenantBranding(tenantId);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ColorsForm>({
    resolver: zodResolver(colorsSchema),
    defaultValues: {
      brand_primary: branding.brand_primary ?? "",
      brand_secondary: branding.brand_secondary ?? "",
    },
  });

  // Reseta o form quando o branding chegar do GET (ou for atualizado).
  useEffect(() => {
    reset({
      brand_primary: branding.brand_primary ?? "",
      brand_secondary: branding.brand_secondary ?? "",
    });
  }, [branding.brand_primary, branding.brand_secondary, reset]);

  const primary = watch("brand_primary");
  const secondary = watch("brand_secondary");

  // Propaga pro preview ao vivo.
  useEffect(() => {
    onPreviewChange(primary, secondary);
  }, [primary, secondary, onPreviewChange]);

  async function onSubmit(data: ColorsForm) {
    update.reset();
    // Backend rejeita PATCH com body {} (422). Se ambos vazios, mandamos
    // null em ambos (limpa registro) — Pydantic aceita pq fields_set vira ≠∅.
    const payload = {
      brand_primary: data.brand_primary === "" ? null : data.brand_primary,
      brand_secondary: data.brand_secondary === "" ? null : data.brand_secondary,
    };
    const updated = await update.mutateAsync(payload);
    reset({
      brand_primary: updated.brand_primary ?? "",
      brand_secondary: updated.brand_secondary ?? "",
    });
  }

  return (
    <Card>
      <h2 className="mb-4 text-base font-semibold text-[var(--color-foreground)]">
        Cores da marca
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <ColorField
          id="brand-primary"
          label="Cor primária"
          hint="Aplicada em botões, destaques e cabeçalhos dos templates."
          value={primary}
          onChange={(v) => setValue("brand_primary", v, { shouldDirty: true })}
          error={errors.brand_primary?.message}
          fallbackPreview={DEFAULT_PRIMARY}
        />
        {/* register oculto pra o RHF rastrear (controlado por setValue). */}
        <input type="hidden" {...register("brand_primary")} />

        <ColorField
          id="brand-secondary"
          label="Cor secundária"
          hint="Tom de apoio (fundo, contraste). Use o tom escuro da marca."
          value={secondary}
          onChange={(v) => setValue("brand_secondary", v, { shouldDirty: true })}
          error={errors.brand_secondary?.message}
          fallbackPreview={DEFAULT_SECONDARY}
        />
        <input type="hidden" {...register("brand_secondary")} />

        {branding.contrast_warnings.length > 0 ? (
          <div
            role="status"
            className="rounded-md border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-4 py-3 text-xs text-[var(--color-warning)]"
          >
            <p className="mb-1 font-medium">Aviso de contraste:</p>
            <ul className="list-disc pl-5">
              {branding.contrast_warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {update.isSuccess ? (
          <div
            role="status"
            className="rounded-md border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]"
          >
            Cores salvas com sucesso.
          </div>
        ) : null}
        {update.isError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
          >
            {update.error instanceof Error
              ? update.error.message
              : "Erro ao salvar. Tente novamente."}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={!isDirty || isSubmitting || update.isPending}
          aria-busy={isSubmitting || update.isPending}
        >
          {update.isPending ? "Salvando…" : "Salvar cores"}
        </Button>
      </form>
    </Card>
  );
}

// ── BrandingPage ───────────────────────────────────────────────────────────

export function BrandingPage() {
  const { user, isLoading: authLoading } = useAuth();
  const tenantId = user?.tenant_id ?? null;
  const branding = useTenantBranding(tenantId);

  const [previewPrimary, setPreviewPrimary] = useState<string>(DEFAULT_PRIMARY);
  const [previewSecondary, setPreviewSecondary] = useState<string>(DEFAULT_SECONDARY);

  function onPreviewChange(p: string, s: string) {
    setPreviewPrimary(p || DEFAULT_PRIMARY);
    setPreviewSecondary(s || DEFAULT_SECONDARY);
  }

  if (authLoading) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="AJUSTES / IDENTIDADE VISUAL" title="Identidade visual">
          <div className="mx-auto max-w-4xl space-y-6">
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  if (!user) return null;

  // super_admin não tem tenant_id — exigiria seletor. Fora do escopo.
  if (!tenantId) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="AJUSTES / IDENTIDADE VISUAL" title="Identidade visual">
          <div className="mx-auto max-w-xl">
            <Card>
              <h2 className="mb-2 text-base font-semibold">Selecione um cliente</h2>
              <p className="mb-4 text-sm text-[var(--color-muted-foreground)]">
                Esta tela edita a identidade visual de um cliente específico.
                Como super_admin, abra a lista de clientes e selecione um pra
                ajustar o branding dele.
              </p>
              <Link to="/clients" className={buttonVariants({ variant: "primary" })}>
                Ir pra clientes
              </Link>
            </Card>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  // Carregando branding
  if (branding.isLoading || !branding.data) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="AJUSTES / IDENTIDADE VISUAL" title="Identidade visual">
          <div className="mx-auto max-w-4xl space-y-6">
            {branding.isError ? (
              <Card>
                <p role="alert" className="text-sm text-[var(--color-destructive)]">
                  {branding.error instanceof Error
                    ? branding.error.message
                    : "Erro ao carregar branding."}
                </p>
              </Card>
            ) : (
              <>
                <Skeleton className="h-72 w-full" />
                <Skeleton className="h-64 w-full" />
              </>
            )}
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const data = branding.data;

  return (
    <RequireAuth>
      <AppLayout breadcrumb="AJUSTES / IDENTIDADE VISUAL" title="Identidade visual">
        <div className="mx-auto max-w-4xl space-y-6">
          <Card>
            <h2 className="mb-4 text-base font-semibold text-[var(--color-foreground)]">
              Logotipos
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <LogoUploader
                variant="light"
                label="Logo claro (sobre fundo claro)"
                currentUrl={data.logo_light_url}
                tenantId={tenantId}
              />
              <LogoUploader
                variant="dark"
                label="Logo escuro (sobre fundo escuro)"
                currentUrl={data.logo_dark_url}
                tenantId={tenantId}
              />
            </div>
          </Card>

          <ColorsCard
            tenantId={tenantId}
            branding={data}
            onPreviewChange={onPreviewChange}
          />

          <BrandingPreview
            primary={previewPrimary}
            secondary={previewSecondary}
            branding={data}
          />
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
